/**
 * RF-08/RN-08/RN-09 do PRD 32 — colagem de blocos na folha de monitoramento (US-06/J-02,
 * web#2116). Módulo puro: coerção por tipo de coluna, descarte agregado do não-parseável,
 * truncamento de largura/altura e o resumo de uma única colagem.
 *
 * Vive no design system, não em `DsAttendance/utils/` — mesma razão de `sheetRowLimits.ts`
 * (ver seu comentário): quem consome é `AttendanceSheetCard.vue` (`Components`), e a direção
 * de camadas de `.claude/rules/architecture.md` (`Modules -> Components`) proíbe um
 * `Component` importar de um `Module`.
 */

import type { ColumnType } from './attendanceSheet.types'
import { clampPastedRowSpan, MAX_SHEET_ROWS } from './sheetRowLimits'

export type PasteCellValue = string | number | boolean

export interface PasteSummary {
  /** RF-08 (1) — quantos valores não foram reconhecidos e descartados. */
  discardedCount: number
  /** RF-08 (2) — quantas colunas do bloco colado não couberam no modelo. */
  truncatedColumnsCount: number
  /** RF-08 (3) — quantas datas foram interpretadas (formato assumido é sempre o mesmo). */
  datesInterpretedCount: number
  /** RF-08 (4) — quantas linhas foram descartadas por bater o teto de linhas. */
  rowsDiscardedByCap: number
}

export interface PasteComputationResult {
  matrix: PasteCellValue[][]
  summary: PasteSummary
}

/** RN-08 — único formato assumido na ambiguidade; `MM/DD/AAAA` nunca é reconhecido como tal. */
export const ASSUMED_DATE_FORMAT = 'DD/MM/AAAA'

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/
const BR_DATE_RE = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2}|\d{4})$/
/**
 * RN-08 (decisão 2026-08-19, minhaescala-product#18) — `HH:MM` continua o formato
 * canônico, mas `H:MM` (uma casa na hora) e `HH:MM:SS` (segundos descartados) também
 * parseiam; ambos normalizam para `HH:MM`. A hora ainda precisa estar em 0–23.
 */
const TIME_RE = /^(\d{1,2}):([0-5]\d)(?::[0-5]\d)?$/

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

/** Dia/mês fora de faixa (ex.: `32/07/2026`) não parseia — validado contra o próprio mês. */
function formatBrDate(day: number, month: number, year: number): string | undefined {
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
    return undefined
  }
  if (month < 1 || month > 12) return undefined
  const daysInMonth = new Date(year, month, 0).getDate()
  if (day < 1 || day > daysInMonth) return undefined
  return `${pad2(day)}/${pad2(month)}/${year}`
}

/**
 * RN-08 — reconhece `DD/MM/AAAA` com `/`, `-` ou `.`, ano de 2 dígitos e ISO 8601;
 * `MM/DD/AAAA` nunca é reconhecido como tal — o primeiro grupo numérico é sempre o dia.
 */
export function parsePastedDate(raw: string): string | undefined {
  const text = raw.trim()

  const isoMatch = text.match(ISO_DATE_RE)
  if (isoMatch) {
    const [, year, month, day] = isoMatch
    return formatBrDate(Number(day), Number(month), Number(year))
  }

  const brMatch = text.match(BR_DATE_RE)
  if (brMatch) {
    const [, dayStr, monthStr, yearStr] = brMatch
    const year = yearStr.length === 2 ? Number(`20${yearStr}`) : Number(yearStr)
    return formatBrDate(Number(dayStr), Number(monthStr), year)
  }

  return undefined
}

/**
 * RN-08 — hora 24h reconhecida em `HH:MM`, `H:MM` (uma casa na hora) e `HH:MM:SS`
 * (segundos descartados); tudo normaliza para `HH:MM`. Qualquer outra coisa não parseia.
 */
export function parsePastedTime(raw: string): string | undefined {
  const text = raw.trim()
  const match = text.match(TIME_RE)
  if (!match) return undefined
  const hour = Number(match[1])
  if (hour < 0 || hour > 23) return undefined
  return `${pad2(hour)}:${match[2]}`
}

/**
 * RN-08 — `'true'/'false'` (qualquer caixa) são o par canônico; `VERDADEIRO`/`FALSO`
 * (Excel pt-BR) e `x`/`1`/`0` também parseiam (decisão 2026-08-19,
 * minhaescala-product#18). Qualquer outra coisa não parseia.
 */
export function parsePastedCheckbox(raw: string): boolean | undefined {
  const text = raw.trim().toLowerCase()
  if (text === 'true' || text === 'verdadeiro' || text === 'x' || text === '1') return true
  if (text === 'false' || text === 'falso' || text === '0') return false
  return undefined
}

interface CoercedCell {
  value: PasteCellValue
  discarded: boolean
  dateInterpreted: boolean
}

/**
 * RN-08 — converte o valor colado ao tipo da coluna de destino. O que não parseia é
 * **descartado** (célula vazia, `false` em checkbox). A assimetria com a digitação
 * (RN-04/RN-08 — digitar preserva o texto bruto) vale para as colunas de texto/CRM/nome:
 * nelas a colagem também preserva o texto, então não há o que descartar. Em HORA os dois
 * caminhos passam a validar pela MESMA `parsePastedTime`: `sheetTimeCellEditor.ts` a chama
 * ao fechar a célula, e hora inválida digitada é descartada como a colada. Colunas fora de
 * `date`/`time`/`checkbox` (texto, CRM, nome, número) permanecem em texto bruto — a
 * colagem nunca dispara autocomplete nem preenchimento cruzado (RN-04).
 */
export function coercePastedCell(
  raw: PasteCellValue,
  columnType: ColumnType | undefined
): CoercedCell {
  const text = String(raw ?? '').trim()
  if (text === '') {
    // RN-08 — célula vazia colada numa coluna de checkbox vira `false` (desmarcado), não
    // `''`; nas demais colunas continua indo direto (não é descarte).
    if (columnType === 'checkbox') {
      return { value: false, discarded: false, dateInterpreted: false }
    }
    return { value: raw, discarded: false, dateInterpreted: false }
  }

  switch (columnType) {
    case 'date': {
      const parsed = parsePastedDate(text)
      return parsed === undefined
        ? { value: '', discarded: true, dateInterpreted: false }
        : { value: parsed, discarded: false, dateInterpreted: true }
    }
    case 'time': {
      const parsed = parsePastedTime(text)
      return parsed === undefined
        ? { value: '', discarded: true, dateInterpreted: false }
        : { value: parsed, discarded: false, dateInterpreted: false }
    }
    case 'checkbox': {
      const parsed = parsePastedCheckbox(text)
      return parsed === undefined
        ? { value: false, discarded: true, dateInterpreted: false }
        : { value: parsed, discarded: false, dateInterpreted: false }
    }
    default:
      return { value: raw, discarded: false, dateInterpreted: false }
  }
}

export interface ComputePasteBlockParams {
  /** Bloco colado, já como valores crus (o chamador extrai `.value` do formato da lib). */
  block: PasteCellValue[][]
  /** Tipo de TODAS as colunas do modelo, na ordem da grade — não só as do destino. */
  columnTypes: (ColumnType | undefined)[]
  startColIndex: number
  startRowIndex: number
  maxRows?: number
}

/**
 * RF-08/RN-08/RN-09 — pipeline completo de uma colagem: trunca linhas ao teto (reusa
 * `clampPastedRowSpan`, #2119 — não reimplementa a aritmética), trunca colunas ao que
 * existe a partir do destino (bloco mais largo que as colunas disponíveis trunca à
 * direita) e coage cada célula ao tipo da coluna. Agnóstico de jspreadsheet: o chamador
 * extrai o valor cru de `{value}` antes de passar aqui.
 */
export function computePasteBlock(params: ComputePasteBlockParams): PasteComputationResult {
  const { block, columnTypes, startColIndex, startRowIndex, maxRows = MAX_SHEET_ROWS } = params

  const availableColumns = Math.max(columnTypes.length - startColIndex, 0)
  const widestRow = block.reduce((max, row) => Math.max(max, row.length), 0)
  const truncatedColumnsCount = Math.max(widestRow - availableColumns, 0)

  const { allowedRowCount, truncatedCount: rowsDiscardedByCap } = clampPastedRowSpan(
    startRowIndex,
    block.length,
    maxRows
  )

  let discardedCount = 0
  let datesInterpretedCount = 0

  const matrix = block.slice(0, allowedRowCount).map((row) =>
    row.slice(0, availableColumns).map((rawCell, colOffset) => {
      const columnType = columnTypes[startColIndex + colOffset]
      const coerced = coercePastedCell(rawCell, columnType)
      if (coerced.discarded) discardedCount += 1
      if (coerced.dateInterpreted) datesInterpretedCount += 1
      return coerced.value
    })
  )

  return {
    matrix,
    summary: { discardedCount, truncatedColumnsCount, datesInterpretedCount, rowsDiscardedByCap }
  }
}

/**
 * RF-08 — um único aviso por colagem, agregando até quatro informações; `undefined`
 * quando não há nada a informar (nenhum toast deve aparecer nesse caso).
 */
export function buildPasteSummaryMessage(
  summary: PasteSummary,
  maxRows: number
): string | undefined {
  const parts: string[] = []

  if (summary.discardedCount > 0) {
    const noun =
      summary.discardedCount === 1
        ? 'valor não reconhecido foi descartado'
        : 'valores não reconhecidos foram descartados'
    parts.push(`${summary.discardedCount} ${noun}.`)
  }

  if (summary.truncatedColumnsCount > 0) {
    const noun =
      summary.truncatedColumnsCount === 1
        ? 'coluna do bloco colado não coube'
        : 'colunas do bloco colado não couberam'
    parts.push(`${summary.truncatedColumnsCount} ${noun} no modelo de folha.`)
  }

  if (summary.datesInterpretedCount > 0) {
    const noun =
      summary.datesInterpretedCount === 1 ? 'data foi interpretada' : 'datas foram interpretadas'
    parts.push(`${summary.datesInterpretedCount} ${noun} como ${ASSUMED_DATE_FORMAT}.`)
  }

  if (summary.rowsDiscardedByCap > 0) {
    const noun = summary.rowsDiscardedByCap === 1 ? 'linha' : 'linhas'
    parts.push(
      `${summary.rowsDiscardedByCap} ${noun} colada(s) não coube(ram) no limite de ${maxRows} e foi(ram) descartada(s).`
    )
  }

  return parts.length > 0 ? parts.join(' ') : undefined
}
